import styled from 'styled-components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus.svg';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/24x24/info.svg';
import { ReactComponent as CloudGraphic } from '@elementary/common-ui/assets/empty-state-cloud.svg';
import {
  Button,
  EmptyStateGraphic,
  FlexCenter,
  FlexColumn,
  InternalLink,
  PageAlert,
} from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import * as api from 'shared/services/api';
import { RULE_ID, NEW_VALUE } from '@elementary/common-ui/constants/urlParams';
import { INTEGRATION_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import { radius, space } from '@elementary/common-ui/style/utils';

const EmptyStateWrapper = styled(FlexCenter)`
  flex-direction: column;
  background: ${({ theme }) => theme.palette.surface.soft};
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${radius('lg')};
  padding: ${space('xxl')};
  gap: ${space('lg')};
`;

const PlusIconStyled = styled(PlusIcon)`
  path {
    fill: currentColor;
  }
`;

type Props = {
  onAddCustomDestination: () => void;
};

const AlertDestinationEmptyState = ({ onAddCustomDestination }: Props) => {
  const navigate = useNavigateWithEnv();
  const envId = useSelectedEnv();

  const hasPermission = useEnvPermission();
  const hasWritePermission = hasPermission('EDIT_ALERT_RULES');

  const {
    data: integrationOptionsData,
    isLoading: isIntegrationOptionsLoading,
  } = useQuery([INTEGRATION_OPTIONS, envId], () =>
    api.fetchAlertIntegrations(),
  );

  const noIntegrations =
    !isIntegrationOptionsLoading && integrationOptionsData?.length === 0;

  if (noIntegrations) {
    return (
      <PageAlert icon={<InfoIcon />}>
        {hasWritePermission ? (
          <InternalLink to="/settings/environments">Connect </InternalLink>
        ) : (
          'Connect '
        )}
        your messaging app to start getting alerts from Elementary.
      </PageAlert>
    );
  }

  return (
    <EmptyStateWrapper>
      <EmptyStateGraphic fallback={<CloudGraphic width="57px" />} />
      <Typography sx={{ color: 'content.tertiary' }}>
        No matching alert destination
      </Typography>
      {hasWritePermission && (
        <FlexColumn sx={{ gap: 1, width: '260px' }}>
          <Button
            startIcon={<PlusIcon />}
            onClick={() => navigate(`/alert-rules?${RULE_ID}=${NEW_VALUE}`)}
          >
            Create new alert rule
          </Button>
          <Button
            variant="outlined"
            startIcon={<PlusIconStyled />}
            onClick={onAddCustomDestination}
          >
            Set custom alert destination
          </Button>
        </FlexColumn>
      )}
    </EmptyStateWrapper>
  );
};

export default AlertDestinationEmptyState;
